'use client';

import { useEffect, useState } from 'react';
import { environment } from '@/config/environment';
import type { IVisitorCountry } from '@/interfaces/visitorCountry';
import { fetchVisitorCountry } from '@/lib/visitor-country';

// Local builds have no CloudFront country header, so the lookup is skipped there.
const isLocalSite = new URL(environment.site.url).hostname === 'localhost';

// One lookup per page load, shared by every component that asks.
let lookup: Promise<string | null> | null = null;

function visitorCountry(): Promise<string | null> {
  lookup ??= fetchVisitorCountry(
    environment.api.phpApiUrl,
    new AbortController().signal
  ).catch(error => {
    console.error('[Visitor country] Lookup failed.', error);
    return null;
  });
  return lookup;
}

/**
 * The visitor's country (CloudFront), for region-specific UI such as which
 * lead-form fields are mandatory or which chat channel to show first.
 * `?country=XX` overrides it for QA, on any build.
 */
export function useVisitorCountry(): IVisitorCountry {
  const [state, setState] = useState<IVisitorCountry>({
    countryCode: null,
    lookedUp: false,
  });

  useEffect(() => {
    const override = new URLSearchParams(window.location.search)
      .get('country')
      ?.toUpperCase();
    if (override && /^[A-Z]{2}$/.test(override)) {
      setState({ countryCode: override, lookedUp: true });
      return;
    }
    if (isLocalSite) {
      setState({ countryCode: null, lookedUp: true });
      return;
    }
    let cancelled = false;
    void visitorCountry().then(countryCode => {
      if (!cancelled) setState({ countryCode, lookedUp: true });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
